<script setup lang="ts">
import { onMounted, onUnmounted, ref, useTemplateRef } from 'vue';
import type { Option } from '@/modules/common/types/option.type.ts';
import type { SelectEvent } from '@/modules/common/types/ui.ts';
import UiDropdownItem from '@/modules/ui/components/ui/UiDropdownItem.vue';
import UiDropdownDivider from '@/modules/ui/components/ui/UiDropdownDivider.vue';

export type DropdownListOption = Option & {
  divider?: boolean;
};

const { options, checkedOptions, isOptionEditable } = defineProps<{
  options: DropdownListOption[];
  checkedOptions?: null | Option | Option[];
  isOptionEditable?: (option: Option) => boolean;
}>();

const emit = defineEmits<{
  select: [event: SelectEvent];
  edit: [option: Option];
}>();

const listRef = useTemplateRef('list');

const active = ref<number | null>(null);

function setActive(index: number | null) {
  active.value = index;

  if (typeof index === 'number') {
    const item = listRef.value?.querySelectorAll('.ui-dropdown-item')[index];
    item?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
}

function isChecked(option: Option) {
  if (Array.isArray(checkedOptions)) {
    return checkedOptions.some((checkedOption) => checkedOption.code === option.code);
  } else {
    return option.code === checkedOptions?.code;
  }
}

function onUp(event: KeyboardEvent) {
  event.preventDefault();

  if (active.value === null) {
    setActive(0);
    return;
  }

  if (active.value > 0) {
    setActive(active.value - 1);
  } else {
    setActive(options.length - 1);
  }
}

function onDown(event: KeyboardEvent) {
  event.preventDefault();

  if (active.value === null) {
    setActive(0);
    return;
  }

  if (active.value < options.length - 1) {
    setActive(active.value + 1);
  } else {
    setActive(0);
  }
}

function onEnter(event: KeyboardEvent) {
  event.preventDefault();

  const index = active.value ?? (options.length ? 0 : null);

  if (index === null) {
    return;
  }

  emit('select', {
    option: options[index],
    checked: isChecked(options[index]),
    byEnter: true,
  });
}

function onKeydown(event: KeyboardEvent) {
  switch (event.key) {
    case 'ArrowUp':
      onUp(event);
      break;
    case 'ArrowDown':
      onDown(event);
      break;
    case 'Enter':
      onEnter(event);
      break;
  }
}

onMounted(() => window.addEventListener('keydown', onKeydown));
onUnmounted(() => window.removeEventListener('keydown', onKeydown));

function onClick(option: Option) {
  emit('select', {
    option,
    checked: isChecked(option),
  });
}

defineExpose({
  setActive,
  getActive: () => active.value,
});
</script>

<template>
  <ul
    ref="list"
    class="ui-dropdown-list"
    tabindex="-1"
  >
    <template
      v-for="(option, index) in options"
      :key="option.code"
    >
      <UiDropdownItem
        type="list"
        tag="li"
        :active="index === active"
        :checked="isChecked(option)"
        :disabled="option.disabled"
        :editable="isOptionEditable?.(option)"
        no-hover
        @click="onClick(option)"
        @edit="emit('edit', option)"
        @mouseenter="active = index"
        @mouseleave="active = null"
      >
        <slot v-bind="option">
          {{ $maybeT(option.label) }}
        </slot>
      </UiDropdownItem>

      <UiDropdownDivider v-if="option.divider" />
    </template>
  </ul>
</template>

<style scoped lang="scss">
.ui-dropdown-list {
  overflow-y: auto;
  max-height: 270px;
}
</style>
